<template>
  <div class="cb">
    <!-- Tipo de condição (só quando há mais de um) -->
    <div v-if="permiteSql" class="cb-section">
      <div class="cb-section__header">
        <h5 class="cb-section__title">Tipo de condição</h5>
        <p class="cb-section__hint">Escolha como as condições {{ donoDaCondicao }} serão avaliadas.</p>
      </div>
      <div class="cb-tipos">
        <SelectableCard
          v-for="opt in tipoCondicaoOpcoes"
          :key="opt.value"
          :value="opt.value"
          :label="opt.label"
          :description="opt.description"
          :icon="opt.icon"
          :selected="conditionType === opt.value"
          @select="updateTipo"
        />
      </div>
    </div>

    <!-- VISUAL -->
    <div v-if="!permiteSql || conditionType === 'visual'" class="cb-section">
      <div class="cb-section__header">
        <h5 class="cb-section__title">Regras visuais</h5>
        <p class="cb-section__hint">Combine grupos de condições. Dentro de cada grupo, escolha se devem valer todas ou qualquer uma.</p>
      </div>

      <div v-if="!fieldsLoaded" class="cb-loading">
        <fa-icon icon="spinner" spin /> Carregando campos do evento...
      </div>

      <div v-else class="cb-visual">
        <div v-if="!grupos.length" class="cb-empty">
          <fa-icon icon="layer-group" class="cb-empty__icon" />
          <p class="cb-empty__title">Nenhum grupo de condição</p>
          <p class="cb-empty__hint">Adicione um grupo para começar a montar suas regras.</p>
        </div>

        <div v-for="(grupo, gIdx) in grupos" :key="gIdx" class="cb-group">
          <div v-if="gIdx > 0" class="cb-connector">
            <span class="cb-connector__line"></span>
            <span class="cb-connector__label">E</span>
            <span class="cb-connector__line"></span>
          </div>

          <div class="cb-group__card">
            <div class="cb-group__header">
              <div class="cb-group__title">
                <span class="cb-group__number">{{ gIdx + 1 }}</span>
                <span>Atende</span>
                <select
                  class="cb-group__logic"
                  :value="grupo.logica"
                  @change="setGrupoLogica(gIdx, $event.target.value)"
                >
                  <option value="AND">todas</option>
                  <option value="OR">qualquer uma</option>
                </select>
                <span>das condições</span>
              </div>
              <button
                type="button"
                class="cb-btn-icon cb-btn-icon--danger"
                @click="removerGrupo(gIdx)"
                title="Remover grupo"
              >
                <fa-icon icon="trash-alt" size="sm" />
              </button>
            </div>

            <div class="cb-conditions">
              <div v-for="(cond, cIdx) in grupo.condicoes" :key="cIdx" class="cb-row">
                <div class="cb-row__prefix">
                  <span v-if="cIdx === 0" class="cb-row__prefix-text">Se</span>
                  <span v-else class="cb-row__prefix-text cb-row__prefix-text--logic">
                    {{ grupo.logica === 'AND' ? 'E' : 'OU' }}
                  </span>
                </div>

                <div class="cb-row__field">
                  <AcaoObjetoCampoSelect
                    :value="cond.campo"
                    :fields="fields"
                    @input="setCondCampo(gIdx, cIdx, $event)"
                  />
                </div>

                <div class="cb-row__operator">
                  <select
                    class="cb-select"
                    :value="cond.operador"
                    :disabled="!cond.campo"
                    @change="setCondOperador(gIdx, cIdx, $event.target.value)"
                  >
                    <option value="">Operador...</option>
                    <option
                      v-for="op in operadoresParaCampo(cond.campo)"
                      :key="op.slug"
                      :value="op.slug"
                    >{{ op.label }}</option>
                  </select>
                </div>

                <div class="cb-row__value">
                  <span v-if="operadorSemValor(cond.operador)" class="cb-badge cb-badge--muted">Sem valor</span>

                  <div v-else-if="operadorRange(cond.operador)" class="cb-between">
                    <ParametroValorInput
                      :value="rangeValor(cond, 'inicio')"
                      :tipo="tipoParaCampo(cond.campo, cond.operador)"
                      :entidade-ref="entidadeParaCampo(cond.campo)"
                      :permite-relativo="permiteRelativoParaCampo(cond.campo, cond.operador)"
                      placeholder="Início"
                      @input="setCondValorRange(gIdx, cIdx, 'inicio', $event)"
                    />
                    <span class="cb-between__sep">e</span>
                    <ParametroValorInput
                      :value="rangeValor(cond, 'fim')"
                      :tipo="tipoParaCampo(cond.campo, cond.operador)"
                      :entidade-ref="entidadeParaCampo(cond.campo)"
                      :permite-relativo="permiteRelativoParaCampo(cond.campo, cond.operador)"
                      placeholder="Fim"
                      @input="setCondValorRange(gIdx, cIdx, 'fim', $event)"
                    />
                  </div>

                  <EntidadeAutocomplete
                    v-else-if="entidadeParaCampo(cond.campo)"
                    :value="cond.valor"
                    :entidade="entidadeParaCampo(cond.campo)"
                    @input="setCondValorFixoEntidade(gIdx, cIdx, $event)"
                  />

                  <ParametroValorInput
                    v-else
                    :value="parametroValor(cond)"
                    :tipo="tipoParaCampo(cond.campo, cond.operador)"
                    :opcoes="opcoesParaCampo(cond.campo)"
                    :permite-relativo="permiteRelativoParaCampo(cond.campo, cond.operador)"
                    @input="setCondValorParametro(gIdx, cIdx, $event)"
                  />
                </div>

                <div class="cb-row__remove">
                  <button
                    type="button"
                    class="cb-btn-icon cb-btn-icon--danger"
                    :disabled="grupo.condicoes.length <= 1"
                    @click="removerCondicao(gIdx, cIdx)"
                    title="Remover condição"
                  >
                    <fa-icon icon="times" size="sm" />
                  </button>
                </div>
              </div>
            </div>

            <button type="button" class="cb-add-condition" @click="adicionarCondicao(gIdx)">
              <fa-icon icon="plus" size="sm" /> Adicionar condição
            </button>
          </div>
        </div>

        <button type="button" class="cb-add-group" @click="adicionarGrupo">
          <fa-icon icon="plus" size="sm" /> Adicionar grupo de condições
        </button>
      </div>
    </div>

    <!-- SQL -->
    <div v-if="permiteSql && conditionType === 'sql'" class="cb-section">
      <div class="cb-section__header">
        <h5 class="cb-section__title">SQL avançado</h5>
        <p class="cb-section__hint">
          Use <code class="cb-code">:campo</code> para referenciar dados do payload.
          A query deve retornar pelo menos 1 linha para a condição passar.
        </p>
      </div>

      <div class="cb-sql-banco">
        <label class="cb-mini-label" for="cb-sql-banco-select">Banco</label>
        <select
          id="cb-sql-banco-select"
          class="form-control cb-input"
          :value="conditionSqlConnection || ''"
          :disabled="!sqlConnections.length"
          @change="$emit('update:conditionSqlConnection', $event.target.value || null)"
        >
          <option v-for="c in sqlConnections" :key="c.slug" :value="c.slug">{{ c.label }}</option>
        </select>
        <span class="cb-sql-banco__hint">
          A query será executada nesta conexão (whitelist do back).
        </span>
      </div>

      <div class="cb-sql-example">
        <span class="cb-sql-example__tag">Exemplo</span>
        <code>SELECT 1 FROM pedido WHERE id = :pedidoId AND status = 'F'</code>
      </div>

      <AcaoObjetoSqlEditor
        :value="conditionSql"
        :fields="fields"
        @input="$emit('update:conditionSql', $event)"
      />

      <div class="cb-sql-actions">
        <button type="button" class="cb-btn cb-btn--secondary cb-btn--sm" @click="validarSql" :disabled="validando">
          <fa-icon icon="check-double" /> {{ validando ? 'Validando...' : 'Validar SQL' }}
        </button>
        <span v-if="validacao" class="cb-sql-validacao" :class="validacao.ok ? 'is-ok' : 'is-erro'">
          <fa-icon :icon="validacao.ok ? 'check-circle' : 'exclamation-triangle'" />
          {{ validacao.ok ? 'SQL válido (EXPLAIN ok).' : validacao.erro }}
        </span>
      </div>
    </div>

  </div>
</template>

<script>
const SEM_VALOR = ['nulo', 'nao_nulo'];
const RANGE_OPERADORES = ['entre'];

// Operadores aplicáveis por tipo de campo. Filtra a lista vinda do backend
// (o backend continua sendo a fonte de verdade — só removemos o que nao faz sentido).
const OPERADORES_POR_TIPO = {
  string:  ['igual', 'diferente', 'contem', 'nao_contem', 'comeca_com', 'termina_com', 'em', 'nao_em', 'nulo', 'nao_nulo'],
  number:  ['igual', 'diferente', 'maior', 'maior_igual', 'menor', 'menor_igual', 'entre', 'em', 'nao_em', 'nulo', 'nao_nulo'],
  date:    ['igual', 'diferente', 'maior', 'maior_igual', 'menor', 'menor_igual', 'entre', 'nulo', 'nao_nulo'],
  boolean: ['igual', 'diferente', 'nulo', 'nao_nulo'],
  fk:      ['igual', 'diferente', 'em', 'nao_em', 'nulo', 'nao_nulo'],
};

// Mapping de campos FK conhecidos -> entidade do EntidadeAutocomplete.
// operacaoId NÃO entra aqui: é tratado como ID numérico comum (input de valor),
// já que não existe entidade "operacao" no EntidadeAutocomplete e o campo de
// valor sumia ao trocar para ele.
const FK_ENTIDADE_MAP = {
  clienteId:        'cliente',
  colaboradorId:    'colaborador',
  marcaId:          'marca',
  produtoId:        'produto',
  transportadoraId: 'transportadora',
};

function inferEntidadeFromName(name) {
  if (!name) return '';
  return FK_ENTIDADE_MAP[name] || '';
}

function inferTipoFromName(name) {
  if (!name) return 'text';
  if (FK_ENTIDADE_MAP[name]) return 'fk';
  const lower = String(name).toLowerCase();
  if (lower.startsWith('data') || lower.startsWith('dthora') || lower.endsWith('_em') || lower.endsWith('_at')) return 'date';
  if (
    lower.startsWith('valor') ||
    lower.startsWith('percentual') ||
    lower.startsWith('perc_') ||
    lower.startsWith('quantidade') ||
    lower.startsWith('qtd') ||
    lower.endsWith('id') || // ids genericos sao numericos (nao FK conhecido)
    lower.endsWith('_id')
  ) return 'number';
  return 'text';
}

import SelectableCard from '@/views/Campanhas/components/CampanhasComerciais/shared/SelectableCard.vue';
import AcaoObjetoSqlEditor from './AcaoObjetoSqlEditor.vue';
import AcaoObjetoCampoSelect from './AcaoObjetoCampoSelect.vue';
import ParametroValorInput from '@/components/Filtros/ParametroValorInput.vue';
import EntidadeAutocomplete from '@/components/Filtros/EntidadeAutocomplete.vue';
import { post, get } from '@/helpers/apiRequest';

const TIPO_CONDICAO_OPCOES = [
  {
    value: 'visual',
    label: 'Visual',
    description: 'Monta condições em grupos com AND/OR usando os campos do evento.',
    icon: 'cil-options',
  },
  {
    value: 'sql',
    label: 'SQL avançado',
    description: 'Escreve uma query SQL livre. Use :campo para referenciar dados do payload.',
    icon: 'cil-code',
  },
];

export default {
  name: 'AcaoObjetoCondicaoBuilder',
  components: { SelectableCard, AcaoObjetoSqlEditor, AcaoObjetoCampoSelect, ParametroValorInput, EntidadeAutocomplete },
  props: {
    conditionType:          { type: String, default: 'visual' },
    /*
     * Onde a condição é avaliada dentro do caminho do usuário — o roteamento de
     * integrações roda durante a cotação de frete, com o cliente esperando —, a
     * consulta livre ao banco não se paga: seriam N consultas ao ERP por
     * operação, uma por instância configurada. Nesses casos o construtor visual
     * é o único modo, e o seletor some em vez de oferecer uma escolha ruim.
     */
    permiteSql:             { type: Boolean, default: true },
    conditionVisual:        { type: Object, default: null },
    conditionSql:           { type: String, default: '' },
    conditionSqlConnection: { type: String, default: null },
    fields:                 { type: Array,  default: () => [] },
    fieldsLoaded:           { type: Boolean, default: true },
    operadores:             { type: Array,  default: () => [] },
    endpointBase:           { type: String, default: '/admin/gatilhos' },
    /*
     * De quem é a condição sendo montada. O componente nasceu para gatilhos,
     * mas atende também o roteamento de integrações — dizer "do gatilho" numa
     * tela de integração confunde quem está configurando frete.
     */
    donoDaCondicao:         { type: String, default: 'do gatilho' },
  },
  data() {
    return {
      tipoCondicaoOpcoes: TIPO_CONDICAO_OPCOES,
      validando: false,
      validacao: null,
      sqlConnections: [],
    };
  },
  mounted() {
    this.loadSqlConnections();
  },
  computed: {
    grupos() {
      const cv = this.conditionVisual;
      return cv && Array.isArray(cv.grupos) ? cv.grupos : [];
    },
  },
  methods: {
    operadorSemValor(op) { return SEM_VALOR.includes(op); },
    operadorRange(op) { return RANGE_OPERADORES.includes(op); },

    // ---- Metadados de campo (tipo / entidade FK) ---------------------------
    campoMeta(campo) {
      const fieldDef = (this.fields || []).find(f => f.name === campo) || {};
      const tipoBase = fieldDef.type || fieldDef.tipo || inferTipoFromName(campo);
      const entidade = fieldDef.entidade || inferEntidadeFromName(campo);
      // Quando o campo eh FK conhecida, normaliza tipo para "fk".
      const tipo = entidade ? 'fk' : tipoBase;
      return { tipo, entidade, opcoes: fieldDef.opcoes || [] };
    },
    /*
     * Campo de dominio fechado (status do pedido, por exemplo) e escolhido numa
     * lista, nao digitado: em texto livre, uma letra fora do lugar produz uma
     * regra que nunca casa e nao acusa erro nenhum.
     */
    opcoesParaCampo(campo) {
      return this.campoMeta(campo).opcoes;
    },
    tipoParaCampo(campo, operador) {
      const meta = this.campoMeta(campo);

      // "entre" pede dois campos e nao combina com dominio fechado; fica com o
      // tipo base para o range continuar funcionando.
      if (meta.opcoes.length && operador !== 'entre' && !this.operadorSemValor(operador)) {
        // "em"/"nao em" comparam com uma lista — o valor e multiplo.
        return ['em', 'nao_em'].includes(operador) ? 'multiselect' : 'select';
      }

      // Mapeia para os tipos aceitos pelo ParametroValorInput
      switch (meta.tipo) {
        case 'date': return 'date';
        case 'number': return 'number';
        case 'boolean': return 'boolean';
        case 'fk': return 'text'; // fallback (entidade tratada por EntidadeAutocomplete)
        default: return 'text';
      }
    },
    entidadeParaCampo(campo) {
      return this.campoMeta(campo).entidade || '';
    },
    permiteRelativoParaCampo(campo, operador) {
      if (this.operadorSemValor(operador)) return false;
      return this.campoMeta(campo).tipo === 'date';
    },
    operadoresParaCampo(campo) {
      const all = this.operadores || [];
      const tipo = this.campoMeta(campo).tipo;
      const permitidos = OPERADORES_POR_TIPO[tipo];
      if (!permitidos || !campo) return all;
      const filtrados = all.filter(op => permitidos.includes(op.slug));
      // Se nada bater (lista do backend nao expõe esses slugs), devolve a lista cheia para nao travar.
      return filtrados.length ? filtrados : all;
    },

    // ---- Helpers de valor para o template ----------------------------------
    parametroValor(cond) {
      // Default tipo_valor = "fixo" para gatilhos antigos (retrocompatibilidade).
      return {
        tipo_valor: cond.tipo_valor || 'fixo',
        valor: cond.valor,
      };
    },
    rangeValor(cond, parte) {
      const v = cond.valor;
      if (v && typeof v === 'object' && (parte in v)) {
        const item = v[parte] || {};
        return {
          tipo_valor: item.tipo_valor || 'fixo',
          valor: item.valor != null ? item.valor : '',
        };
      }
      return { tipo_valor: 'fixo', valor: '' };
    },

    updateTipo(v) { this.$emit('update:conditionType', v); },

    emitVisual(grupos) {
      this.$emit('update:conditionVisual', { grupos });
    },
    cloneGrupos() {
      return JSON.parse(JSON.stringify(this.grupos));
    },
    adicionarGrupo() {
      const grupos = this.cloneGrupos();
      grupos.push({ logica: 'AND', condicoes: [{ campo: '', operador: 'igual', tipo_valor: 'fixo', valor: '' }] });
      this.emitVisual(grupos);
    },
    removerGrupo(gIdx) {
      const apply = () => {
        const grupos = this.cloneGrupos();
        grupos.splice(gIdx, 1);
        this.emitVisual(grupos);
      };
      if (this.grupos.length <= 1) {
        this.$swal({
          title: 'Remover último grupo?',
          text: 'O gatilho ficará sem condições visuais se você remover este grupo.',
          showCancelButton: true,
          icon: 'warning',
          confirmButtonText: 'Remover',
          cancelButtonText: 'Cancelar',
        }).then(res => {
          if (!res.value) return;
          apply();
        });
        return;
      }
      apply();
    },
    setGrupoLogica(gIdx, val) {
      const grupos = this.cloneGrupos();
      grupos[gIdx].logica = val;
      this.emitVisual(grupos);
    },
    adicionarCondicao(gIdx) {
      const grupos = this.cloneGrupos();
      grupos[gIdx].condicoes.push({ campo: '', operador: 'igual', tipo_valor: 'fixo', valor: '' });
      this.emitVisual(grupos);
    },
    removerCondicao(gIdx, cIdx) {
      const grupos = this.cloneGrupos();
      grupos[gIdx].condicoes.splice(cIdx, 1);
      this.emitVisual(grupos);
    },
    setCondCampo(gIdx, cIdx, val) {
      const grupos = this.cloneGrupos();
      const cond = grupos[gIdx].condicoes[cIdx];
      cond.campo = val;
      const meta = this.campoMeta(val);
      // Propaga entidade_ref quando FK conhecida.
      if (meta.entidade) cond.entidade_ref = meta.entidade;
      else delete cond.entidade_ref;
      // Se o operador atual nao for valido para o novo tipo, volta para "igual".
      const permitidos = OPERADORES_POR_TIPO[meta.tipo];
      if (permitidos && cond.operador && !permitidos.includes(cond.operador)) {
        cond.operador = 'igual';
      }
      // Trocando o tipo de campo, reseta o valor para evitar lixo (ex.: range -> fixo).
      cond.valor = '';
      cond.tipo_valor = 'fixo';
      this.emitVisual(grupos);
    },
    setCondOperador(gIdx, cIdx, val) {
      const grupos = this.cloneGrupos();
      const cond = grupos[gIdx].condicoes[cIdx];
      const era = cond.operador;
      cond.operador = val;
      // Trocando entre/range <-> simples, reseta valor para evitar shape misturado.
      const eraRange = RANGE_OPERADORES.includes(era);
      const ehRange = RANGE_OPERADORES.includes(val);
      if (eraRange !== ehRange) {
        cond.valor = ehRange ? { inicio: { tipo_valor: 'fixo', valor: '' }, fim: { tipo_valor: 'fixo', valor: '' } } : '';
        if (!ehRange) cond.tipo_valor = 'fixo';
      }
      this.emitVisual(grupos);
    },
    setCondValorParametro(gIdx, cIdx, payload) {
      // payload = { tipo_valor, valor }
      const grupos = this.cloneGrupos();
      const cond = grupos[gIdx].condicoes[cIdx];
      cond.tipo_valor = payload.tipo_valor || 'fixo';
      cond.valor = payload.valor != null ? payload.valor : '';
      // TODO(C8): VisualConditionEvaluator ainda nao interpreta tipo_valor=relativo.
      // Por enquanto o front grava o shape novo e o evaluator trata o slug como literal.
      this.emitVisual(grupos);
    },
    setCondValorFixoEntidade(gIdx, cIdx, valEntidade) {
      const grupos = this.cloneGrupos();
      const cond = grupos[gIdx].condicoes[cIdx];
      cond.tipo_valor = 'fixo';
      cond.valor = valEntidade != null ? valEntidade : '';
      const meta = this.campoMeta(cond.campo);
      if (meta.entidade) cond.entidade_ref = meta.entidade;
      this.emitVisual(grupos);
    },
    setCondValorRange(gIdx, cIdx, parte, payload) {
      const grupos = this.cloneGrupos();
      const cond = grupos[gIdx].condicoes[cIdx];
      const atual = (cond.valor && typeof cond.valor === 'object') ? cond.valor : {};
      cond.valor = {
        inicio: atual.inicio || { tipo_valor: 'fixo', valor: '' },
        fim: atual.fim || { tipo_valor: 'fixo', valor: '' },
      };
      cond.valor[parte] = {
        tipo_valor: payload.tipo_valor || 'fixo',
        valor: payload.valor != null ? payload.valor : '',
      };
      // tipo_valor da condicao em si fica "fixo" (cada parte carrega o seu)
      cond.tipo_valor = 'fixo';
      this.emitVisual(grupos);
    },

    loadSqlConnections() {
      get(`${this.endpointBase}/sql-connections`)
        .then(json => {
          this.sqlConnections = Array.isArray(json) ? json : [];
          // Se nao há slug definido (gatilho novo ou legado sem coluna),
          // adota o primeiro slug retornado (provavelmente "local").
          if (!this.conditionSqlConnection && this.sqlConnections.length) {
            this.$emit('update:conditionSqlConnection', this.sqlConnections[0].slug);
          }
        })
        .catch(() => { this.sqlConnections = []; });
    },
    validarSql() {
      if (!this.conditionSql || !this.conditionSql.trim()) {
        this.validacao = { ok: false, erro: 'Digite um SQL para validar.' };
        return;
      }
      this.validando = true;
      this.validacao = null;
      post(`${this.endpointBase}/sql-explain`, { sql: this.conditionSql })
        .then(json => { this.validacao = json; })
        .catch(err => { this.validacao = { ok: false, erro: err.message || 'Falha de rede.' }; })
        .finally(() => { this.validando = false; });
    },
  },
};
</script>

<style scoped>
.cb-section {
  background: #fff;
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  padding: 16px 18px;
  margin-bottom: 14px;
}
.cb-section__header { margin-bottom: 12px; }
.cb-section__title {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: #1d2939;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.cb-section__hint {
  margin: 2px 0 0;
  font-size: 12px;
  color: #667085;
  line-height: 1.5;
}
.cb-code {
  background: #f2f2f8;
  color: #3a3e81;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
}

.cb-tipos {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}
@media (max-width: 768px) {
  .cb-tipos { grid-template-columns: 1fr; }
}

/* Inputs */
.cb-input {
  border-radius: 6px !important;
  border: 1px solid #dddce9 !important;
  font-size: 13px !important;
  background: #fff;
  transition: all 0.15s ease;
}
.cb-input:focus {
  border-color: #3a3e81 !important;
  box-shadow: 0 0 0 3px rgba(58, 62, 129, 0.1) !important;
}
.cb-input--disabled {
  display: flex;
  align-items: center;
  padding: 7px 12px;
  background: #f9fafb;
  color: #98a2b3;
  font-style: italic;
  font-size: 12px;
  height: 36px;
}
.cb-textarea {
  font-family: 'Menlo', 'Monaco', 'Consolas', monospace;
  font-size: 12px !important;
  background: #fafbfc;
}
.cb-mini-label {
  display: block;
  font-size: 10px;
  font-weight: 600;
  color: #98a2b3;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 4px;
}

/* Visual builder (estilo tag-filtro) */
.cb-visual {
  background: #f4f6f9;
  border: 1px solid #d8dbe0;
  border-radius: 8px;
  padding: 16px;
}

.cb-group { position: relative; }

.cb-connector {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 12px 0;
}
.cb-connector__line {
  flex: 1;
  height: 1px;
  background: #c4c9d0;
}
.cb-connector__label {
  font-size: 11px;
  font-weight: 700;
  color: #768192;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 10px;
  background: #ebedef;
  border-radius: 10px;
}

.cb-group__card {
  background: #fff;
  border: 1px solid #d8dbe0;
  border-radius: 8px;
  overflow: hidden;
}
.cb-group__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #f8f9fb;
  border-bottom: 1px solid #ebedef;
}
.cb-group__title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #3c4b64;
}
.cb-group__number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  background: #3a3e81;
  border-radius: 50%;
  flex-shrink: 0;
}
.cb-group__logic {
  appearance: none;
  -webkit-appearance: none;
  padding: 2px 24px 2px 8px;
  font-size: 13px;
  font-weight: 600;
  color: #3a3e81;
  background: #eceaf3 url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='6'%3E%3Cpath d='M0 0l4 6 4-6z' fill='%233a3e81'/%3E%3C/svg%3E") no-repeat right 8px center;
  border: 1px solid #b8b9d0;
  border-radius: 4px;
  cursor: pointer;
  outline: none;
}
.cb-group__logic:focus {
  border-color: #3a3e81;
  box-shadow: 0 0 0 2px rgba(58, 62, 129, 0.15);
}

.cb-conditions { padding: 12px 14px 4px; }

/*
 * A linha de condição junta três componentes de origens diferentes — o seletor
 * de campo, o select de operador e o campo de valor dos filtros —, cada um com
 * a sua altura. Alinhados pelo topo, as bases ficavam em três níveis.
 *
 * ALTURA_CONTROLE (36px) é a altura do seletor de campo e do select de
 * operador; o campo de valor vem do Bootstrap com `form-control-sm`, mais
 * baixo, e é igualado aqui — no escopo da linha, para não mexer nos filtros que
 * usam o mesmo componente em outras telas.
 */
.cb-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.cb-row__prefix {
  width: 32px;
  flex-shrink: 0;
  text-align: right;
}
.cb-row__prefix-text {
  font-size: 13px;
  font-weight: 600;
  color: #768192;
}
.cb-row__prefix-text--logic { color: #3a3e81; }
.cb-row__field { width: 220px; flex-shrink: 0; }
.cb-row__operator { width: 160px; flex-shrink: 0; }
.cb-row__value { flex: 1; min-width: 0; display: flex; align-items: center; }
.cb-row__remove { flex-shrink: 0; }

/* Iguala a altura do campo de valor à dos outros dois controles da linha. */
.cb-row__value >>> .form-control,
.cb-row__value >>> .form-control-sm,
.cb-row__value >>> .multi-select-container {
  height: 36px;
  min-height: 36px;
}

.cb-row__value >>> .multi-select-container {
  display: flex;
  align-items: center;
}


.cb-select {
  display: block;
  width: 100%;
  height: 36px;
  padding: 4px 28px 4px 10px;
  font-size: 13px;
  color: #3c4b64;
  background-color: #fff;
  border: 1px solid #d8dbe0;
  border-radius: 4px;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='6'%3E%3Cpath d='M0 0l4 6 4-6z' fill='%23768192'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.cb-select:disabled { background-color: #ebedef; color: #9da5b1; cursor: not-allowed; }
.cb-select:focus { border-color: #9496b8; box-shadow: 0 0 0 2px rgba(58, 62, 129, 0.12); }

.cb-between {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
}
.cb-between__sep {
  font-size: 13px;
  font-weight: 500;
  color: #768192;
  flex-shrink: 0;
}

.cb-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 4px;
  white-space: nowrap;
}
.cb-badge--muted { background: #ebedef; color: #768192; }

.cb-btn-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 4px;
  background: transparent;
  color: #9da5b1;
  cursor: pointer;
  transition: all 0.15s;
}
.cb-btn-icon:hover { color: #3c4b64; background: #ebedef; }
.cb-btn-icon--danger:hover { color: #e55353; background: #fce4e4; border-color: #f5c2c2; }
.cb-btn-icon:disabled { opacity: 0.35; cursor: not-allowed; }
.cb-btn-icon:disabled:hover { background: transparent; color: #9da5b1; }

.cb-add-condition {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 4px 14px 12px;
  padding: 5px 14px;
  font-size: 13px;
  font-weight: 500;
  color: #3a3e81;
  background: transparent;
  border: 1px dashed #9496b8;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s;
}
.cb-add-condition:hover { background: #eceaf3; border-color: #3a3e81; }

.cb-add-group {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  margin-top: 12px;
  padding: 10px;
  font-size: 13px;
  font-weight: 500;
  color: #768192;
  background: #fff;
  border: 1px dashed #c4c9d0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
}
.cb-add-group:hover { color: #3a3e81; border-color: #9496b8; background: #f2f2f8; }

@media (max-width: 768px) {
  .cb-row { flex-wrap: wrap; }
  .cb-row__field, .cb-row__operator { width: 100%; }
}

/* Buttons */
.cb-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 16px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s ease;
  font-family: inherit;
}
.cb-btn--sm { padding: 7px 13px; font-size: 11px; }
.cb-btn--primary {
  background: #3a3e81;
  color: #fff;
  box-shadow: 0 2px 6px rgba(58, 62, 129, 0.2);
}
.cb-btn--primary:hover { background: #2f326b; }
.cb-btn--ghost {
  background: #fff;
  border-color: #dddce9;
  color: #3a3e81;
}
.cb-btn--ghost:hover {
  background: #f2f2f8;
  border-color: #3a3e81;
}
.cb-btn--secondary {
  background: #edf8f9;
  color: #2298a3;
  border-color: #c5e6e9;
}
.cb-btn--secondary:hover { background: #2298a3; color: #fff; border-color: #2298a3; }

.cb-sql-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
  flex-wrap: wrap;
}
.cb-sql-validacao {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 4px;
}
.cb-sql-validacao.is-ok { background: #ecfaee; color: #2e7d32; border: 1px solid #b9e7be; }
.cb-sql-validacao.is-erro { background: #fdecea; color: #b3261e; border: 1px solid #e89189; }

.cb-icon-btn {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid;
  background: #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  font-size: 11px;
}
.cb-icon-btn--danger { border-color: #fbd7d6; color: #d9534f; }
.cb-icon-btn--danger:hover { background: #d9534f; color: #fff; border-color: #d9534f; }

/* SQL banco selector */
.cb-sql-banco {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 10px;
  max-width: 360px;
}
.cb-sql-banco__hint {
  font-size: 11px;
  color: #98a2b3;
}

/* SQL example */
.cb-sql-example {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: #fef6e6;
  border: 1px solid #fce4b3;
  border-radius: 8px;
  margin-bottom: 10px;
  font-family: 'Menlo', 'Monaco', monospace;
  font-size: 11px;
  color: #7a4f00;
  overflow-x: auto;
}
.cb-sql-example__tag {
  background: #b85f00;
  color: #fff;
  padding: 2px 7px;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-family: inherit;
  flex-shrink: 0;
}

/* Empty / loading */
.cb-empty {
  text-align: center;
  padding: 32px 16px;
  color: #98a2b3;
}
.cb-empty__icon {
  font-size: 28px;
  color: #d0d5dd;
  margin-bottom: 10px;
  opacity: 0.6;
}
.cb-empty__title {
  font-size: 13px;
  font-weight: 600;
  color: #475467;
  margin: 6px 0 2px;
}
.cb-empty__hint {
  font-size: 12px;
  color: #98a2b3;
  margin: 0;
}
.cb-loading {
  text-align: center;
  padding: 24px;
  color: #667085;
  font-size: 13px;
}
</style>
